package dev.danko.roblox_craft_ai.client

import dev.danko.roblox_craft_ai.shared.model.AdminAuditDto
import dev.danko.roblox_craft_ai.shared.model.AdminGrantCreditsRequest
import dev.danko.roblox_craft_ai.shared.model.AccessRequestAdminDto
import dev.danko.roblox_craft_ai.shared.model.AdminMeDto
import dev.danko.roblox_craft_ai.shared.model.AdminUsageReport
import dev.danko.roblox_craft_ai.shared.model.AdminUserDto
import dev.danko.roblox_craft_ai.shared.model.AdminUserStatusRequest
import dev.danko.roblox_craft_ai.shared.model.BillingPlanDto
import dev.danko.roblox_craft_ai.shared.model.CreditPackDto
import dev.danko.roblox_craft_ai.shared.model.ErrorResponse
import dev.danko.roblox_craft_ai.shared.model.ModerationIncidentDto
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

/** Admin API. Failures throw [ApiException] with the server's message, so the page can show validation errors. */
object AdminApiClient {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private val client = HttpClient { install(ContentNegotiation) { json(json) } }
    private val base: String get() = BackendEndpoint.httpBase + "/api/v1/admin"

    private suspend fun HttpResponse.check(): HttpResponse {
        if (status.isSuccess()) return this
        val text = runCatching { bodyAsText() }.getOrDefault("")
        val parsed = runCatching { json.decodeFromString(ErrorResponse.serializer(), text) }.getOrNull()
        throw ApiException(parsed?.error_code ?: "HTTP_${status.value}", parsed?.message ?: status.description)
    }

    suspend fun isAdmin(): Boolean = try {
        client.get("$base/me").check().body<AdminMeDto>().isAdmin
    } catch (_: Throwable) {
        false
    }

    suspend fun usage(days: Int): AdminUsageReport = client.get("$base/usage?days=$days").check().body()

    suspend fun plans(): List<BillingPlanDto> = client.get("$base/plans").check().body()

    suspend fun savePlan(plan: BillingPlanDto): BillingPlanDto = client.put("$base/plans/${plan.id}") {
        contentType(ContentType.Application.Json)
        setBody(plan)
    }.check().body()

    suspend fun packs(): List<CreditPackDto> = client.get("$base/packs").check().body()

    suspend fun createPack(pack: CreditPackDto): CreditPackDto = client.post("$base/packs") {
        contentType(ContentType.Application.Json)
        setBody(pack)
    }.check().body()

    suspend fun savePack(pack: CreditPackDto): CreditPackDto = client.put("$base/packs/${pack.id}") {
        contentType(ContentType.Application.Json)
        setBody(pack)
    }.check().body()

    suspend fun users(query: String): List<AdminUserDto> =
        client.get("$base/users") { parameter("query", query.trim().takeIf { it.isNotEmpty() }) }.check().body()

    suspend fun grantCredits(userId: String, credits: Int, note: String?) {
        client.post("$base/users/$userId/credits") {
            contentType(ContentType.Application.Json)
            setBody(AdminGrantCreditsRequest(credits, note))
        }.check()
    }

    suspend fun setStatus(userId: String, status: String) {
        client.post("$base/users/$userId/status") {
            contentType(ContentType.Application.Json)
            setBody(AdminUserStatusRequest(status))
        }.check()
    }

    suspend fun incidents(openOnly: Boolean): List<ModerationIncidentDto> =
        client.get("$base/moderation/incidents") { if (openOnly) parameter("status", "open") }.check().body()

    suspend fun resolveIncident(id: Int) {
        client.post("$base/moderation/incidents/$id/resolve").check()
    }

    suspend fun audit(): List<AdminAuditDto> = client.get("$base/audit").check().body()

    suspend fun accessRequests(status: String?): List<AccessRequestAdminDto> =
        client.get("$base/access-requests" + (status?.let { "?status=$it" } ?: "")).check().body()

    suspend fun deleteUser(id: String) {
        client.post("$base/users/$id/delete").check()
    }

    suspend fun approveAccess(id: Long): AccessRequestAdminDto = client.post("$base/access-requests/$id/approve").check().body()

    suspend fun rejectAccess(id: Long): AccessRequestAdminDto = client.post("$base/access-requests/$id/reject").check().body()
}
